import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WeekNav } from '../components/Shared/WeekNav';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { addWeeks, startOfWeek, endOfWeek } from 'date-fns';
import CalendarModal from '../components/ShoppingList/CalendarModal';
import RecipeList from '../components/ShoppingList/RecipeList';
import IngredientList, { Item } from '../components/ShoppingList/IngredientList';
import fetchWithAuth from '../utils/fetchWithAuth';
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner';
import ProgressConflictModal from '../components/ShoppingList/ProgressConflictModal';
import { FaCartPlus } from 'react-icons/fa6';
import FixedBottomCTA from '../components/ui/atoms/Button/FixedBottomCTA';
import { scaledAmountParts } from '../utils/unitDisplay';

type DateRange = { startDate: string | null; endDate: string | null };

type ExistingListInfo = {
  exists: boolean;
  createdAt: string;
  totalItems: number;
  checkedCount: number;
};

const ShoppingListPrepPage: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [timeRange, setTimeRange] = useState<'week' | 'custom'>('week');
  const [selectedDates, setSelectedDates] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });
  const [recipes, setRecipes] = useState<any[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<'recipes' | 'ingredients'>('ingredients');
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [existingInfo, setExistingInfo] = useState<ExistingListInfo | null>(null);

  // whether there is anything to create (after removes)
  const canCreate = useMemo(
    () => items.some((it) => !it.isRemoved),
    [items]
  );

  // Compute Monday–Sunday around `base`
  const computeWeekRange = (base: Date): DateRange => ({
    startDate: startOfWeek(base, { weekStartsOn: 1 }).toISOString(),
    endDate: endOfWeek(base, { weekStartsOn: 1 }).toISOString(),
  });

  // Fetch summary whether a list exists for this range
  const fetchExistingListInfo = async (
    startDate: string,
    endDate: string
  ): Promise<ExistingListInfo> => {
    return fetchWithAuth(
      `/api/shopping-list/progress?dateStart=${encodeURIComponent(
        startDate
      )}&dateEnd=${encodeURIComponent(endDate)}`
    );
  };

  // Fetch recipes + ingredients for a range
  const fetchMeals = async (start: string, end: string) => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(
        `/api/meal-plan/for-range?dateStart=${encodeURIComponent(
          start
        )}&dateEnd=${encodeURIComponent(end)}`
      );
      setRecipes(data.recipes || []);

      setItems(
        (data.aggregatedIngredients || []).map((ing: any) => {
          const isQualitative = ing.unit === 'to taste' || typeof ing.requiredAmount !== 'number';
          return {
            ingredientId:  ing.ingredientId,
            name:           ing.name,
            unit:           ing.unit,
            // always a number so the POST passes validation:
            requiredAmount:  typeof ing.requiredAmount === 'number'
                      ? ing.requiredAmount
                      : 0,
            haveAmount:     0,
            isRemoved:      false,
            mode:           'active' as const,
            isCustom:       false,
          };
        })
      );
    } catch (e) {
      console.error('Failed to fetch meal plan:', e);
    } finally {
      setLoading(false);
    }
  };

  // On mount → load this week
  useEffect(() => {
    const range = computeWeekRange(new Date());
    setSelectedDates(range);
    fetchMeals(range.startDate!, range.endDate!);
  }, []);

  // When custom range selected
  useEffect(() => {
    if (
      timeRange === 'custom' &&
      selectedDates.startDate &&
      selectedDates.endDate
    ) {
      fetchMeals(selectedDates.startDate, selectedDates.endDate);
    }
  }, [timeRange, selectedDates]);

  // Open calendar for custom range
  const openCustomModal = () => {
    setTimeRange('custom');
    setIsModalOpen(true);
  };

  // 1) User clicks "Create Shopping List"
  const handleCreateShoppingList = async () => {
    const { startDate, endDate } = selectedDates;
    if (!startDate || !endDate) return;

    // nothing to create → do nothing
    if (!items.some((it) => !it.isRemoved)) {
      return;
    }

    setLoading(true);
    const info = await fetchExistingListInfo(startDate, endDate);
    if (info.exists) {
      setLoading(false);
      setExistingInfo(info);
      setShowProgressModal(true);
      return;
    }

    // No existing list → create right away
    await actuallyCreateList(false);
  };

  // 2) Actually POST, with optional override
  const actuallyCreateList = async (override: boolean = false) => {
    const { startDate, endDate } = selectedDates;
    if (!startDate || !endDate) return;

    setLoading(true);
    const payload = items
      .filter(it => !it.isRemoved)
      .map(it => ({
        ingredientId: it.ingredientId,
        name: it.name,
        unit: it.unit,
        requiredAmount: it.requiredAmount,
        haveAmount: it.haveAmount || 0,
        isCustom: it.isCustom,
      }));

    // still nothing? bail
    if (payload.length === 0) {
      setLoading(false);
      return;
    }

    try {
      await fetchWithAuth(`/api/shopping-list/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dateStart: startDate,
          dateEnd: endDate,
          items: payload,
          override,
        }),
      });
      navigate(
        `/app/shopping-list/view?dateStart=${encodeURIComponent(
          startDate
        )}&dateEnd=${encodeURIComponent(endDate)}`
      );
    } catch (e) {
      console.error('Failed to create shopping list:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* ── Fixed Header: WeekNav + Calendar ── */}
        {/* <div className="fixed top-0 inset-x-0 z-50 bg-white h-[72px] px-4 flex items-center justify-center gap-3" id="shopping-list-header"> */}
        <div
          className="fixed inset-x-0 z-50 bg-white h-[72px] px-4 flex items-center justify-center gap-3"
          style={{ top: 'var(--safe-top)' }}
        >
          <WeekNav
            weekStart={new Date(selectedDates.startDate!)}
            weekEnd={new Date(selectedDates.endDate!)}
            isCustom={timeRange === 'custom'}
            fullWidth={false}
            onClickThisWeek={() => {
              const range = computeWeekRange(new Date());
              setTimeRange('week');
              setSelectedDates(range);
              fetchMeals(range.startDate!, range.endDate!);
            }}
            onChangeWeek={offset => {
              setTimeRange('week');
              const baseDate =
                offset === 0
                  ? new Date()
                  : addWeeks(new Date(selectedDates.startDate!), offset);
              const range = computeWeekRange(baseDate);
              setSelectedDates(range);
              fetchMeals(range.startDate!, range.endDate!);
            }}
          />
          or
          <button
            onClick={openCustomModal}
            className="p-3 rounded-full flex-shrink-0 bg-white text-primary border border-1 border-gray-300"
          >
            <FaRegCalendarAlt className="w-5 h-5" />
          </button>
        </div>

        {/* ── Sticky Tabs ── */}
        {/* <div className="sticky top-[65px] z-40 bg-white border-b grid grid-cols-2 h-[40px] px-4"> */}
        <div
          className="sticky inset-x-0 bg-white border-b grid grid-cols-2 h-[40px] px-4 z-40"
          style={{ top: 'calc(var(--safe-top) + 72px)' }}
        >
          <button
            onClick={() => setSelectedTab('ingredients')}
            className={`w-full text-center h-full text-sm flex items-center justify-center ${
              selectedTab === 'ingredients'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500'
            }`}
          >
            Ingredients
          </button>
          <button
            onClick={() => setSelectedTab('recipes')}
            className={`w-full text-center h-full text-sm flex items-center justify-center ${
              selectedTab === 'recipes'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500'
            }`}
          >
            Recipes
          </button>
        </div>

        {/* ── Scrollable Content ── */}
        {/* <div className="pt-[74px] pb-[140px] px-4"> */}
        <div
          className="pb-[140px] px-4 max-w-md mx-auto"
          style={{ paddingTop: '72px' }}
        >
          <CalendarModal
            isOpen={isModalOpen}
            onSelectRange={r => {
              setTimeRange('custom');
              setSelectedDates(r);
              setIsModalOpen(false);
            }}
            onClose={() => setIsModalOpen(false)}
          />

          {loading ? (
            <BrandSpinner />
          ) : selectedTab === 'ingredients' ? (
            <IngredientList
              items={items}
              setItems={setItems}
              formatQuantity={(req, have, unit) => {
                const u = (unit || '').toLowerCase();

                // qualitative labels: never show a number
                if (u === 'to taste' || u === 'as needed') {
                  return unit || '';
                }

                // non-numeric, missing, or zero → just show the unit (no "0 unit")
                if (req == null || typeof req !== 'number' || req <= 0) {
                  return unit || '';
                }

                const diff = Math.max(0, req - (have || 0));
                const { amountStr, unitStr } = scaledAmountParts(diff, 1, 1, unit || '');
                return `${amountStr} ${unitStr}`.trim();
              }}
            />
          ) : (
            <RecipeList recipes={recipes} />
          )}
        </div>

        {/* ── Bottom CTA ── */}
        {canCreate && (
          <FixedBottomCTA
            onClick={handleCreateShoppingList}
            icon={<FaCartPlus className="w-5 h-5" />}
            text="Create Shopping List"
          />
        )}

        {/* ── Progress Conflict Modal ── */}
        {showProgressModal && existingInfo && (
          <ProgressConflictModal
            isOpen={showProgressModal}
            info={existingInfo}
            onClose={() => setShowProgressModal(false)}
            onGoToList={() =>
              navigate(
                `/app/shopping-list/view?dateStart=${encodeURIComponent(
                  selectedDates.startDate!
                )}&dateEnd=${encodeURIComponent(
                  selectedDates.endDate!
                )}`
              )
            }
            onCreateNew={() => {
              setShowProgressModal(false);
              actuallyCreateList(true);
            }}
          />
        )}
      </div>
    </>
  );
};

export default ShoppingListPrepPage;
